import React, { useRef, useState, useEffect, useCallback } from 'react';
import QrScanner from 'qr-scanner';

const QRScanner = ({ onScanSuccess, onScanError }) => {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const isProcessingRef = useRef(false); // ✅ Prevent multiple scans
  
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // ✅ Memoize handleScan to prevent recreating on every render
  const handleScan = useCallback((result) => {
    // Prevent processing if already handling a scan
    if (isProcessingRef.current) {
      console.log('⏭️ Already processing a scan, skipping...');
      return;
    }

    console.log('📸 QR Code detected:', result.data);
    
    // Mark as processing
    isProcessingRef.current = true;

    try {
      // Parse JSON data
      const parsed = JSON.parse(result.data);
      
      // Validate structure
      if (!parsed.txRef || !parsed.type) {
        throw new Error('Invalid QR code format. Missing txRef or type.');
      }
      
      if (parsed.type !== 'PAYMENT') {
        throw new Error('Invalid QR code type. Expected PAYMENT.');
      }
      
      // Validate txRef format (alphanumeric, 1-100 chars)
      const txRef = String(parsed.txRef).trim();
      if (!/^[a-zA-Z0-9_-]{1,100}$/.test(txRef)) {
        throw new Error('Invalid transaction reference format.');
      }
      
      console.log('✅ Valid payment QR code:', txRef);
      
      // Stop scanning before calling success callback
      stopScanning();
      
      if (onScanSuccess) {
        onScanSuccess(txRef);
      }
      
    } catch (err) {
      console.error('❌ QR validation error:', err);
      setError(err.message);
      
      // Reset processing flag on error
      isProcessingRef.current = false;
      
      if (onScanError) {
        onScanError(err);
      }
      
      // Clear error after 2 seconds
      setTimeout(() => {
        setError(null);
      }, 2000);
    }
  }, [onScanSuccess, onScanError]);

  // ✅ Initialize scanner when user clicks start
  useEffect(() => {
    if (hasStarted && videoRef.current && !scannerRef.current) {
      startScanner();
    }

    // Cleanup on unmount or when stopping
    return () => {
      if (scannerRef.current) {
        try {
          scannerRef.current.stop();
          scannerRef.current.destroy();
        } catch (e) {
          console.error('Cleanup error:', e);
        }
        scannerRef.current = null;
      }
    };
  }, [hasStarted]);

  // ✅ Start scanner
  const startScanner = async () => {
    setIsInitializing(true);
    setError(null);
    isProcessingRef.current = false; // Reset processing flag

    try {
      // Stop any existing scanner
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }

      console.log('📹 Initializing QR Scanner...');

      // ✅ Create scanner with the memoized callback
      const scanner = new QrScanner(
        videoRef.current,
        handleScan, // ✅ Pass the callback directly
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment',
          maxScansPerSecond: 5, // ✅ Increase scan rate
          calculateScanRegion: (video) => {
            // ✅ Define scan region (center 80% of video)
            const smallestDimension = Math.min(video.videoWidth, video.videoHeight);
            const scanRegionSize = Math.round(0.8 * smallestDimension);
            return {
              x: Math.round((video.videoWidth - scanRegionSize) / 2),
              y: Math.round((video.videoHeight - scanRegionSize) / 2),
              width: scanRegionSize,
              height: scanRegionSize,
            };
          }
        }
      );

      // Start scanner
      await scanner.start();
      
      scannerRef.current = scanner;
      setIsScanning(true);
      setIsInitializing(false);
      
      console.log('✅ QR Scanner started and active');

    } catch (err) {
      console.error('❌ Camera access error:', err);
      setIsInitializing(false);
      
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera access to scan QR codes.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found. Please ensure your device has a camera.');
      } else if (err.name === 'NotReadableError') {
        setError('Camera is already in use by another application.');
      } else {
        setError('Failed to access camera: ' + err.message);
      }
      
      setHasStarted(false);
      
      if (onScanError) {
        onScanError(err);
      }
    }
  };

  // ✅ Start button clicked
  const handleStartClick = () => {
    setError(null);
    setHasStarted(true);
  };

  // ✅ Stop scanning
  const stopScanning = () => {
    if (scannerRef.current) {
      try {
        scannerRef.current.stop();
        scannerRef.current.destroy();
      } catch (e) {
        console.error('Stop error:', e);
      }
      scannerRef.current = null;
    }
    setIsScanning(false);
    setHasStarted(false);
    setIsInitializing(false);
    isProcessingRef.current = false;
  };

  return (
    <div className="qr-scanner-container">
      {hasStarted ? (
        <>
          {/* Camera preview box */}
          <div className="scanner-box">
            <video
              ref={videoRef}
              className="scanner-video"
              playsInline
              muted
              autoPlay
            />
            
            {/* Loading overlay */}
            {isInitializing && (
              <div className="scanner-loading">
                <div className="spinner" />
                <p>Initializing camera...</p>
              </div>
            )}
            
            {/* Scanning overlay */}
            {isScanning && !isInitializing && (
              <div className="scanner-overlay">
                <div className="scanner-frame" />
                <p className="scanner-text">Position QR code within the frame</p>
              </div>
            )}
          </div>
          
          {/* Error message */}
          {error && (
            <div className="alert alert-error">{error}</div>
          )}
          
          {/* Stop button */}
          <div className="scanner-controls">
            <button 
              onClick={stopScanning} 
              className="btn btn-secondary btn-lg"
              disabled={isInitializing}
            >
              Stop Scanning
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Error message */}
          {error && (
            <div className="alert alert-error">{error}</div>
          )}
          
          {/* Start button */}
          <div className="scanner-controls">
            <button 
              onClick={handleStartClick} 
              className="btn btn-primary btn-lg"
            >
              Start Scanning
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default QRScanner;